import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import AssignSelection from './AssignSelection';
import DueDateSelection from './DueDateSelection';
import ReactQuill from 'react-quill';
import dotsThreeIcon from 'assets/img/DotsThree.svg';
import style from './Tasks.module.css';
import APIServices from 'services/APIServices';
import { CustomToggleforChecklist, selectLoader, titleLimit } from 'utils/Helpers';
import { Dropdown } from 'react-bootstrap';
import CustomDatepicker from '../../shared/CustomDatepicker/CustomDatepicker';

import delete3 from 'assets/img/delete3.svg';
import edit2 from 'assets/img/edit2.svg';

export default function Task({
  milestone,
  title,
  isCompleted,
  handleChecked,
  dueDate,
  disabled,
  assignees,
  statusOptions,
  status,
  isSubTask,
  handleStatusChange,
  isEditable,
  members,
  handleAddAssignee,
  handleRemoveAssignee,
  handleDueDateChange,
  fetchSubtask,
  handleTitleChange,
  handleSetEditableTask,
  handleSaveTask,
  dropdownOpen,
  onDropdownToggle,
  handleDeleteTask,
  handleDisableTask,
  cancelNewItem,
  reminderOptions
}) {
  const clickTimeoutRef = useRef(null);
  const reactQuillRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  // Regex to detect URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  const handleClick = () => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    // Single click opens subtasks (if not editing)
    clickTimeoutRef.current = setTimeout(() => {
      if (isSubTask && !isEditable) {
        fetchSubtask();
      }
    }, 250);
  };

  const handleDoubleClick = () => {
    clearTimeout(clickTimeoutRef.current);
    handleSetEditableTask();
  };

  // Fetch link preview and replace raw URL
  const handleLinkPreview = async (url, index) => {
    try {
      setIsLoading(true);
      const response = await APIServices.post('/link/preview', { url });

      if (response?.title) {
        const quill = reactQuillRef.current.getEditor();

        // Remove raw URL and replace with hyperlink title
        quill.deleteText(index, url.length);
        quill.insertText(index, response.title, { link: url });
      }
    } catch (err) {
      console.error('Error fetching link preview:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle text changes inside ReactQuill
  const handleTextChange = (content, delta, source, editor) => {
    const plainText = editor.getText().trim();

    // ✅ enforce char limit of 20
    if (plainText.length > titleLimit) {
      const truncated = plainText.substring(0, titleLimit);

      // force truncate content in editor
      const quill = reactQuillRef?.current?.getEditor();
      quill?.deleteText(0, quill?.getLength());
      quill?.insertText(0, truncated);

      handleTitleChange(truncated);
      return;
    }

    handleTitleChange(content);

    if (source === 'user') {
      const matches = [...plainText.matchAll(urlRegex)];

      if (matches.length > 0) {
        const lastMatch = matches[matches.length - 1];
        const url = lastMatch[0];
        const index = lastMatch.index;
        handleLinkPreview(url, index);
      }
    }
  };

  const getPlainText = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  // Inside your Tasks component
  useEffect(() => {
    if (isEditable && reactQuillRef.current) {
      const quill = reactQuillRef.current.getEditor();
      quill.focus();
      // ✅ Move caret to the end of text
      const length = quill.getLength();
      quill.setSelection(length, 0);
    }
  }, [isEditable]);

  const hasAnchorTag = (html) => {
    if (!html) return false;
    return /<a\s/i.test(html);
  };

  const handleLabelClick = (e) => {
    if (e.target.closest('a')) {
      return;
    }
    if (hasAnchorTag(title)) return;

    handleDoubleClick();
  };

  return (
    <div className={style.task_wrapper} style={{ userSelect: 'none' }}>
      <Form className={style.form_check_wrapper}>
        {/* Checkbox */}
        {!isSubTask && (
          <Form.Check
            type="checkbox"
            checked={isCompleted || false}
            onChange={handleChecked}
            className={`${isCompleted ? style.active : ''} form-check`}
            disabled={disabled}
          />
        )}

        {/* Title Section */}
        {isEditable && !disabled ? (
          <>
            <div className="w-100">
              <ReactQuill
                ref={reactQuillRef}
                value={title || ''}
                onChange={handleTextChange}
                className={`mb-0 ml-1 ${style.title_form_editor}`}
                placeholder="Enter Title"
                modules={{ toolbar: false }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    handleDisableTask(); // ✅ exit edit mode
                  }
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSaveTask(); // optional: save on Enter
                  }
                }}
              />
            </div>
            {isLoading && <small className="text-muted ms-2">{selectLoader(20)}</small>}
          </>
        ) : isSubTask ? (
          <label
            onClick={handleClick}
            className={`mb-0 ${style.title_label}  ${isCompleted ? style.strikethrough : ''}`}>
            {getPlainText(title)}
          </label>
        ) : (
          <label
            dangerouslySetInnerHTML={{ __html: title }}
            className={`mb-0 ${style.title_label} ${isCompleted ? style.strikethrough : ''}`}
            onClick={handleLabelClick}
          />
        )}
      </Form>

      {/* Right Side Controls */}
      <div className={`d-flex gap-3 align-items-center time-left-content ${style.hover_parent}`}>
        <AssignSelection
          assignees={assignees}
          members={members}
          handleMember={(val) => handleAddAssignee(val._id)}
          handleRemove={(val) => handleRemoveAssignee(val)}
          renderTop={true}
          disabled={disabled}
          showRemove={true}
          isFromModal={true}
        />

        {isSubTask && (
          <div className={style.select_wrapper_todo}>
            <Select
              className={`${style.select_todo} select-todo`}
              options={statusOptions}
              value={statusOptions.find((option) => option.value === status)}
              onChange={(val) => handleStatusChange(val.value)}
              menuPosition="fixed"
              isDisabled={disabled}
              //menuIsOpen={true}
            />
          </div>
        )}

        {/* <DueDateSelection
          showTimeSelect={true}
          handleDueDate={handleDueDateChange}
          dueDate={dueDate}
          isDisabled={disabled}
        /> */}
        <CustomDatepicker
          defaultDate={dueDate}
          handleDueDate={handleDueDateChange}
          reminderOptionsData={reminderOptions}
          milestone={milestone}
          showDateOnly={true}
          viewMode={disabled}
        />
        {!disabled && (
          <div className={style.actions_drp}>
            <Dropdown
              align="end"
              show={dropdownOpen} // controlled by parent
              onToggle={(isOpen) => onDropdownToggle(isOpen)} // notify parent
            >
              <Dropdown.Toggle as={CustomToggleforChecklist} id="dropdown-custom-components">
                <img src={dotsThreeIcon} alt="menu" />
              </Dropdown.Toggle>

              <Dropdown.Menu className={style.dropdown_menu}>
                <Dropdown.Item
                  onClick={() => {
                    handleSetEditableTask();
                    cancelNewItem();
                  }}
                  eventKey="1">
                  <img src={edit2} alt="edit2" className="featherdrowpdown-icon" />
                  Edit
                </Dropdown.Item>
                <Dropdown.Item onClick={handleDeleteTask} eventKey="2">
                  <img src={delete3} alt="delete3" className="featherdrowpdown-icon" />
                  Delete
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}
      </div>
    </div>
  );
}
